import * as React from "react";

import { ConfigProperties, socialIcons, socialNames, SocialNetworksId, TEXT_ONLY_SOCIAL_NETWORKS } from "@/interfaces";
import { ConfigContext } from "@/contexts/appContexts";
import {
  addYears,
  useSocialCoachSession,
  useSocialNetworks,
  useSocialProfiles,
  validateMessageLenghtInSocialNetworks,
} from "@/utils";
import { Field, Label, RadioGroup, Tooltip, Button, Skeleton, Checkbox } from "@/components/ui";
import { SocialNetworkIconButton } from "@/components/SocialNetworkIconButton";
import { DateTimePicker } from "@/components";
import { cn } from "@/lib/utils";
import { usePostFlowContext } from "../SharedPostFlowContext";
import { useEffect } from "react";

export function DeliveryOptionsStep() {
  const { isPlayer, userId } = useSocialCoachSession();
  const config: ConfigProperties = React.useContext(ConfigContext);
  const {
    deliveryOptions,
    setDeliveryOptions,
    showVideoTypeSelector,
    mediaGroup,
    changeMediaGroupType,
    media,
    message,
  } = usePostFlowContext();

  const deliveryScheduled = deliveryOptions.sendMode === "SCHEDULED";
  const { postableSocialNetworks } = useSocialNetworks();
  const hasMedia = media.length > 0;
  const { networksActiveForPosting, isLoading: isLoadingProfiles } = useSocialProfiles(isPlayer ? userId : undefined);

  const availableNetworks = React.useMemo(() => {
    const coachNetworks = postableSocialNetworks.filter(sn => hasMedia || TEXT_ONLY_SOCIAL_NETWORKS.includes(sn));
    if (isPlayer) {
      return coachNetworks.filter(sn => networksActiveForPosting.includes(sn));
    } else {
      return coachNetworks;
    }
  }, [postableSocialNetworks, networksActiveForPosting, isPlayer]);

  useEffect(() => {
    if (availableNetworks && availableNetworks.length > 0 && deliveryOptions.socialNetworks.length == 0) {
      setDeliveryOptions({ ...deliveryOptions, socialNetworks: availableNetworks });
    }
  }, [availableNetworks]);

  const handleSelectAll = () => {
    const socialNetworksValidation = validateMessageLenghtInSocialNetworks(message, config.post!!);

    const validAvailableNetworks = availableNetworks.filter(sn => {
      const isDisabled =
        socialNetworksValidation.includes(socialNames[sn]) ||
        (sn === "TW" && socialNetworksValidation.includes("Twitter"));
      return !isDisabled;
    });

    setDeliveryOptions({ ...deliveryOptions, socialNetworks: validAvailableNetworks });
  };

  const socialNetworkWithValidation = (sn: SocialNetworksId, index: number) => {
    const socialNetworksValidation = validateMessageLenghtInSocialNetworks(message, config.post!!);

    const isDisabled =
      socialNetworksValidation.includes(socialNames[sn]) ||
      (sn === "TW" && socialNetworksValidation.includes("Twitter"));

    return isDisabled ? (
      <Tooltip key={"post_sn_tooltip_" + index}>
        <Tooltip.Trigger>
          <div className="opacity-50 grayscale cursor-not-allowed">
            <img src={socialIcons[sn]} className="size-8 object-contain" alt={socialNames[sn]} />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>Because the caption is too long, it cannot be posted to {socialNames[sn]}.</Tooltip.Content>
      </Tooltip>
    ) : (
      <SocialNetworkIconButton
        disabled={isDisabled}
        size={"noMargin"}
        key={"post_sn_icon_" + index}
        active={deliveryOptions.socialNetworks.includes(sn)}
        networkId={sn}
        onClickHandler={checked => {
          const newSocialNetworks = [...deliveryOptions.socialNetworks];
          if (checked && !newSocialNetworks.includes(sn)) {
            newSocialNetworks.push(sn);
          } else if (!checked && newSocialNetworks.includes(sn)) {
            newSocialNetworks.splice(newSocialNetworks.indexOf(sn), 1);
          }
          setDeliveryOptions({ ...deliveryOptions, socialNetworks: newSocialNetworks });
        }}
      />
    );
  };

  /*
   * Auto Post or Push Notification
   */
  const deliveryType = () => {
    return (
      <Field>
        <Field.Label>Publishing Method</Field.Label>
        <Field.Description>
          {isPlayer ? "How would you like this published?" : "Choose how you want to deliver this content."}
        </Field.Description>
        <Field.Content>
          <RadioGroup
            value={deliveryOptions.isAutoPost ? "autoPost" : "pushNotification"}
            onValueChange={val => {
              setDeliveryOptions({ ...deliveryOptions, isAutoPost: val === "autoPost" });
            }}
            className="flex flex-col gap-4 pl-4"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value="autoPost" id="autoPost" />
                <Label
                  htmlFor="autoPost"
                  className="text-slate-800 font-semibold cursor-pointer font-sans flex flex-row gap-2"
                >
                  <div>{isPlayer ? "Post automatically" : "Auto-Post"}</div>
                  {!isLoadingProfiles && availableNetworks.length !== deliveryOptions.socialNetworks.length && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sm text-primary hover:text-primary/80"
                      onClick={handleSelectAll}
                    >
                      [ Select All ]
                    </Button>
                  )}
                </Label>
              </div>
              {deliveryOptions.isAutoPost && (
                <div className="pl-6 flex flex-col gap-2 mt-1">
                  <div className="flex items-center gap-2">
                    {!isLoadingProfiles && deliveryOptions.socialNetworks.length == 0 && (
                      <div className="text-sm text-muted-foreground">Select at least one social network:</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 items-center h-10">
                    {isLoadingProfiles ? (
                      <>
                        <Skeleton className="size-8 rounded-full" />
                        <Skeleton className="size-8 rounded-full" />
                        <Skeleton className="size-8 rounded-full" />
                        <Skeleton className="size-8 rounded-full" />
                      </>
                    ) : (
                      <>
                        {availableNetworks.map((sn, index) => socialNetworkWithValidation(sn, index))}
                        {showVideoTypeSelector && mediaGroup && (
                          <Checkbox
                            id="showVideoTypeSelector"
                            checked={mediaGroup?.type == "REELS"}
                            label="Post as Reels?"
                            className={"ml-2"}
                            onCheckedChange={checked => {
                              if (checked) changeMediaGroupType("REELS");
                              else changeMediaGroupType("VIDEO");
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="pushNotification" id="pushNotification" />
              <Label htmlFor="pushNotification" className="text-slate-900 font-semibold cursor-pointer">
                {isPlayer ? "Send a reminder to my phone" : "Push Notification"}
              </Label>
            </div>
          </RadioGroup>
        </Field.Content>
      </Field>
    );
  };

  const deliveryModeSection = () => {
    return (
      <Field>
        <Field.Label>Scheduling Options</Field.Label>
        <Field.Description>
          {deliveryOptions.isAutoPost ? "When would you like to schedule this?" : "Choose a time for delivery."}
        </Field.Description>
        <Field.Content>
          <RadioGroup
            value={deliveryOptions.sendMode}
            onValueChange={val => {
              setDeliveryOptions({ ...deliveryOptions, sendMode: val });
            }}
            className="flex flex-col gap-4 pl-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="IMMEDIATE" id="IMMEDIATE" />
              <Label htmlFor="IMMEDIATE" className="test-slate-800 font-semibold cursor-pointer">
                {deliveryOptions.isAutoPost ? "Post" : "Send"} Now
              </Label>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value="SCHEDULED" id="SCHEDULED" />
                <Label htmlFor="SCHEDULED" className="text-slate-900 font-semibold cursor-pointer">
                  {deliveryOptions.isAutoPost ? "Post" : "Send"} Later
                </Label>
              </div>
              {deliveryScheduled && (
                <div className="pl-6 mt-2">
                  <DateTimePicker
                    selectedDate={deliveryOptions.date}
                    selectedTime={deliveryOptions.time}
                    onChangeDate={date => {
                      setDeliveryOptions({ ...deliveryOptions, date: date });
                    }}
                    onChangeTime={time => {
                      setDeliveryOptions({ ...deliveryOptions, time: time });
                    }}
                    minDate={new Date()}
                    maxDate={addYears(new Date(), 1)}
                  />
                </div>
              )}
            </div>
          </RadioGroup>
        </Field.Content>
      </Field>
    );
  };

  return (
    <div className={cn("relative min-h-[100px]")}>
      <div className="flex flex-col gap-8">
        {deliveryType()}
        {deliveryModeSection()}
      </div>
    </div>
  );
}
